'use client';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { cn } from '@repo/ui/lib/utils';

interface AnimatedValueProps {
  /** Rendered value; a change animates the old value out and the new one in. */
  value: string;
  className?: string;
}

/**
 * Fast crossfade for live numeric values. Background polling means numbers
 * can tick every minute across many surfaces at once — the update must read
 * as the number quietly staying current, never as the page re-rendering, so
 * the transition is a barely-there 150ms fade with no movement. Honors
 * prefers-reduced-motion by rendering statically.
 */
export function AnimatedValue({ value, className }: AnimatedValueProps) {
  const reducedMotion = useReducedMotion();

  if (reducedMotion) {
    return <span className={className}>{value}</span>;
  }

  return (
    <span className={cn('relative inline-grid', className)}>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={value}
          className="whitespace-nowrap [grid-area:1/1]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'linear' }}
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
